import { z } from 'zod';

export const PROVIDERS = ['line', 'facebook', 'instagram', 'tiktok', 'telegram', 'sms', 'mock'] as const;
export type Provider = (typeof PROVIDERS)[number];

export const CONNECTION_STATUSES = [
  'disconnected',
  'connecting',
  'pending_approval',
  'active',
  'degraded',
  'token_expired',
  'revoked',
  'unsupported',
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

/** A capability is either confirmed from provider docs/live checks, or disabled with a reason. */
export const capabilityFlag = z.object({
  supported: z.boolean(),
  /** true only after checking current official docs or a live account check */
  verified: z.boolean(),
  reason: z.string().optional(),
});
export type CapabilityFlag = z.infer<typeof capabilityFlag>;

export const RECEIPT_GRANULARITY = ['none', 'aggregate', 'watermark', 'per_message'] as const;

export const channelCapabilitiesSchema = z.object({
  schemaVersion: z.literal(1),
  inbound: capabilityFlag,
  outboundText: capabilityFlag,
  outboundImage: capabilityFlag,
  historySync: capabilityFlag,
  deliveryReceipts: capabilityFlag.extend({ granularity: z.enum(RECEIPT_GRANULARITY) }),
  readReceipts: capabilityFlag.extend({ granularity: z.enum(RECEIPT_GRANULARITY) }),
  bulkSend: capabilityFlag.extend({ mode: z.enum(['none', 'per_recipient', 'multicast']), maxPerRequest: z.number().int().positive().optional() }),
  idempotentSend: capabilityFlag,
  statusLookup: capabilityFlag,
  quotaSupported: capabilityFlag,
  messagingWindow: z.object({
    kind: z.enum(['none', 'hours_since_inbound', 'reply_token']),
    hours: z.number().positive().optional(),
    note: z.string().optional(),
  }),
  eligibilityRuleVersion: z.string(),
  mediaLimits: z.object({
    imageMaxBytes: z.number().int().positive().optional(),
    previewMaxBytes: z.number().int().positive().optional(),
    imageMimeTypes: z.array(z.string()).default([]),
    requiresHttpsUrl: z.boolean().default(false),
    maxUrlLength: z.number().int().positive().optional(),
  }),
  rateLimits: z
    .array(
      z.object({
        scope: z.enum(['account', 'recipient', 'endpoint']),
        name: z.string(),
        capacity: z.number().positive(),
        refillPerSecond: z.number().positive(),
        source: z.string(),
      }),
    )
    .default([]),
  checkedAt: z.string(),
  sourceUrls: z.array(z.string()).default([]),
  notesTh: z.array(z.string()).default([]),
});
export type ChannelCapabilities = z.infer<typeof channelCapabilitiesSchema>;

export const PROVIDER_LABELS_TH: Record<Provider, string> = {
  line: 'LINE OA',
  facebook: 'Facebook Messenger',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  telegram: 'Telegram Bot',
  sms: 'SMS (Boost SMS)',
  mock: 'DEMO (จำลอง)',
};

export const CONNECTION_STATUS_LABELS_TH: Record<ConnectionStatus, string> = {
  disconnected: 'ยังไม่เชื่อมต่อ',
  connecting: 'กำลังเชื่อมต่อ',
  pending_approval: 'รอสิทธิ์จากแพลตฟอร์ม',
  active: 'ใช้งานได้',
  degraded: 'ทำงานไม่เต็มที่',
  token_expired: 'Token หมดอายุ',
  revoked: 'ถูกยกเลิกสิทธิ์',
  unsupported: 'ไม่รองรับ',
};

/** Short platform word used in "ลูกค้า Facebook 12" — the name of a customer the platform has not named yet. */
export const CHAT_PLACEHOLDER_LABELS: Record<Provider, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  line: 'LINE',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  sms: 'SMS',
  mock: 'DEMO',
};

export function chatPlaceholderName(provider: Provider, n: number): string {
  return `ลูกค้า ${CHAT_PLACEHOLDER_LABELS[provider]} ${n}`;
}

const PLACEHOLDER_NUMBERED = new RegExp(`^ลูกค้า (?:${Object.values(CHAT_PLACEHOLDER_LABELS).join('|')}) [0-9]+$`);

/**
 * A name the system gave a chat customer before the platform told us the real one: "ลูกค้า Facebook 12"
 * (v1.29.0) or the older "ลูกค้า Facebook Messenger …1234".
 */
export function isChatPlaceholderName(name: string): boolean {
  return PLACEHOLDER_NUMBERED.test(name) || /^ลูกค้า .+ …\S{1,8}$/.test(name);
}
